import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { AbstractRoomTool } from "./tools";
import { RouteInteractors } from './interactors';
import { Route as mdlRoute } from "../mdl/Route";
import { RouteEnding } from "../mdl/Route";
import { RouteSnapping } from './RouteSnapping';
import { Util } from "../utils/Util";
import { PointerInfo, PointerButton } from "@egr/wcf/modules/core/view/InputState";
import { InsertElements } from "@egr/wcf/modules/core/cmd";
import { InteractionMode } from "@egr/wcf/modules/core/tool";
import { eq } from "@egr/wcf/modules/utils/math";
import { isPointOnSegment2, VECTOR2_X } from "@egr/wcf/modules/core/utils/MathUtils";
export class Route extends AbstractRoomTool {
    constructor(pRoomManager) {
        super(pRoomManager);
        this.mRouteSnapping = new RouteSnapping(this.mRoomManager);
    }
    start(pParameter) {
        this.mRoute = null;
        this.mView = null;
        this.mLengthFeedback = null;
        this.mTouchMode = false;
        this.mMayCreateRoute = false;
        this.setInteractionMode(InteractionMode.Primary);
        this.mRouteSnapping.prepareSnapping();
        this.mApp.model.beginSelectionChange();
        //this.mRoomManager.beginInteraction();
        return (super.start(pParameter));
    }
    stop() {
        if (this.mRoute != null) {
            this.mRoute._setConnection(RouteEnding.Start, null, null);
            this.mRoute.dispose();
            this.mRoute = null;
        }
        super.stop();
        this.clearFeedback();
        this.mRoomManager.endInteraction();
        this.mApp.model.endSelectionChange();
    }
    handlePointerDown(pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent)) {
            //disable route creation if user uses more than one finger (e.g. zoom)
            if (this.mRoute == null)
                this.mMayCreateRoute = false;
            return;
        }
        let tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        let tPos = this.getPlanePickPos2d(tPointer.position);
        let tFirstDownEvent = this.mRoute == null;
        if (tFirstDownEvent) {
            this.mView = this.mRoomManager.view;
            this.mTouchMode = pState.pointerType !== "mouse";
        }
        if (this.mTouchMode) {
            this.mMayCreateRoute = true;
        }
        else if (tFirstDownEvent)
            this.startRoute(tPos, null);
        else
            this.finishRoute(false, false);
    }
    handlePointerUp(pEvent, pState) {
        let tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        if (pState.isPrimaryPointer(pEvent) && this.mTouchMode && this.mMayCreateRoute) {
            //create default route if touch pointer wasn't moved
            let tDist = Vector2.Distance(tPointer.initialPosition, tPointer.position);
            this.finishRoute(eq(tDist, 0), true);
        }
        else {
            this.mMayCreateRoute = false;
            //stop on right mouse click
            if (PointerInfo.IsMousePointer(pEvent) && tPointer.initialButton === PointerButton.Right) {
                let tDist = Vector2.Distance(tPointer.initialPosition, tPointer.position);
                if (eq(tDist, 0))
                    this.stop();
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    handlePointerMove(pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent) && !pState.isMouseHoverPointer(pEvent))
            return;
        let tPointerPos = null;
        if (pState.isMouseHoverPointer(pEvent))
            tPointerPos = this.mApp.viewer.inputState.pointerPosition;
        else {
            let tPointer = pState.pointers.get(pEvent.pointerId);
            if (tPointer == null)
                return;
            tPointerPos = tPointer.position;
            //start route creation in touch mode
            if (tPointer != null && this.mRoute == null && this.mTouchMode && this.mMayCreateRoute) {
                let tDist = Vector2.Distance(tPointer.initialPosition, tPointer.position);
                if (tDist > 10) {
                    let tPos1 = this.getPlanePickPos2d(tPointer.initialPosition);
                    this.startRoute(tPos1, null);
                }
            }
        }
        let tPos = this.getPlanePickPos2d(tPointerPos);
        if (tPos != null) {
            this.updatePointerPosition(tPos);
            this.updateFeedback();
        }
    }
    prepareFeedback() {
        if (this.mRoute != null) {
            this.mLengthFeedback = new RouteInteractors(this.mRoomManager, this.mRoute);
            this.mLengthFeedback._updateLengthInteractors();
        }
    }
    clearFeedback() {
        if (this.mLengthFeedback != null) {
            this.mLengthFeedback.clear();
            this.mLengthFeedback = null;
        }
        this.mRouteSnapping.clear();
    }
    updateFeedback() {
        if (this.mLengthFeedback != null) {
            this.mLengthFeedback._updateLengthInteractors();
        }
    }
    updatePointerPosition(pPos) {
        if (this.mRoute != null) {
            let tPos = this.mRouteSnapping.snap(pPos, this.mView);
            if (this.mRoomManager.shiftPressed) {
                let tStart = Util.to3d(this.mRoute.startPoint);
                tPos = Util.calcSnapPoint(tStart, tPos, 45);
            }
            this.mRoute.setEndPoint(Util.to2d(tPos));
        }
        else {
            this.mRouteSnapping.snap(pPos, this.mRoomManager.hoverView);
        }
        this.mApp.viewer.requestRenderFrame();
    }
    startRoute(pPos, pPrevRoute) {
        if (pPos == null || this.mView == null)
            return;
        let tPos = this.mRouteSnapping.snap(pPos, this.mView);
        this.mRoute = new mdlRoute(this.mRoomManager);
        this.mRoute.setStartPoint(new Vector2(tPos.x, tPos.z));
        this.mRoute.setEndPoint(new Vector2(tPos.x + 0.001, tPos.z));
        this.mRouteSnapping.prepareSnapping(this.mRoute, RouteEnding.Start);
        if (pPrevRoute != null) {
            this.mRoute.setThickness(pPrevRoute.thickness);
            //this.mRoute.setHeight(pPrevRoute.height);
            pPrevRoute._setConnection(RouteEnding.End, this.mRoute, RouteEnding.Start);
        }
        else {
            //find potential connection route and use height and thickness
            let tNearestRoute = this.mRoomManager.getNearestRoute(this.mRoute.startPoint, this.mRoute);
            if (tNearestRoute != null && isPointOnSegment2(this.mRoute.startPoint, tNearestRoute.startPoint, tNearestRoute.endPoint)) {
                this.mRoute.setThickness(tNearestRoute.thickness);
                //this.mRoute.setHeight(tNearestRoute.height);
            }
        }
        this.prepareFeedback();
        this.mApp.viewer.requestRenderFrame();
    }
    finishRoute(pCreateDefaultRoute, pTouchMode) {
        if (this.mRoute == null) {
            this.stop();
            return;
        }
        let tNewRoute = this.mRoute;
        this.mRoute = null;
        this.clearFeedback();
        if (pCreateDefaultRoute)
            tNewRoute.setEndPoint(tNewRoute.startPoint.add(VECTOR2_X));
        let tCmd = new InsertElements(this.mApp, [tNewRoute]);
        this.mApp.commands.executeCommand(tCmd).then(() => {
            //setup connection
            if (!pCreateDefaultRoute) {
                tNewRoute._findNewConnection(RouteEnding.Start);
                tNewRoute._findNewConnection(RouteEnding.End);
                this.mApp.rendering.notifyModelInteraction();
            }
            this.mApp.model.setSelection([tNewRoute]);
            if (pCreateDefaultRoute || pTouchMode || tNewRoute.endCxn.route != null)
                this.stop();
            else
                this.startRoute(Util.to3d(tNewRoute.endPoint), tNewRoute);
        });
    }
}
